import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, AlertCircle, Loader } from 'lucide-react'
import { authService } from '../../services/auth'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')


  const handleWalletLogin = async () => {
    setLoading(true)
    setError('')

    try {
      // 检查MetaMask是否可用
      if (typeof window.ethereum === 'undefined') {
        throw new Error('请安装MetaMask钱包')
      }

      // 请求账户访问
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      })

      if (accounts.length === 0) {
        throw new Error('请连接钱包')
      }

      const walletAddress = accounts[0]

      // 先检查设备绑定
      setError('正在检查设备绑定...')
      const isDeviceBound = await authService.checkDeviceBinding(walletAddress)
      
      if (!isDeviceBound) {
        throw new Error('该钱包地址未绑定CoreTrust设备，无法登录。请确保您已购买并激活CoreTrust手机。')
      }

      // 创建签名消息
      const message = `DeepLink登录\n\n钱包地址: ${walletAddress}\n时间: ${new Date().toISOString()}`
      
      // 请求签名
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [message, walletAddress]
      })

      // 发送钱包登录请求
      await authService.walletLogin({
        walletAddress,
        signature,
        message
      })

      onSuccess?.()
      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              登录账户
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
              >
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700 dark:text-red-400 text-sm">{error}</span>
              </motion.div>
            )}

            {/* Wallet Login Only */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-white rounded-full"></div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                使用钱包登录
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                请确保您的钱包地址已绑定CoreTrust设备
              </p>

              <button
                onClick={handleWalletLogin}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center font-medium"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <div className="w-5 h-5 mr-2 bg-white rounded-full"></div>
                )}
                {loading ? '连接中...' : '连接钱包'}
              </button>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                需要MetaMask或其他Web3钱包
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default LoginModal